import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { TicketService } from '../../ticket.service';
import { weekDayOptions } from '../../../../../../core/enums/week-day.enum';
import { parseHoursLabel } from '../../../../../../core/helpers/duration.helper';
import { CompetenceHoursPreview, CompetenceSchedulePayload } from '../../../../../../core/models/ticket.model';

/**
 * Bloc « période + jours travaillés + heures quotidiennes » d'un ticket COMPETENCE (PRED-3286),
 * partagé par la modale de création et la modale Motif / Usage. Le formulaire parent doit porter
 * les contrôles `periodStart`, `periodEnd`, `workedDays` (WeekDayEnum[]) et `dailyHours` (libellé « 7h45 »).
 * Le total d'heures est calculé par le back (jours fériés inclus) via l'endpoint de prévisualisation.
 */
@Component({
  selector: 'competence-schedule-fields',
  templateUrl: './competence-schedule-fields.component.html',
})
export class CompetenceScheduleFieldsComponent implements OnInit, OnDestroy {
  @Input() form: UntypedFormGroup;
  @Input() minDate: Date;

  readonly weekDayOptions = weekDayOptions;
  preview: CompetenceHoursPreview | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly ticketService: TicketService) {}

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        startWith(null),
        debounceTime(300),
        map(() => this.buildPayload()),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        switchMap(payload => {
          if (!payload) {
            this.preview = null;
            return [];
          }
          return this.ticketService.previewCompetenceHours(payload).pipe(map(res => res.data));
        }),
        filter(Boolean),
        takeUntil(this.destroy$),
      )
      .subscribe(preview => (this.preview = preview));
  }

  /** Payload de prévisualisation, ou null tant que les 4 champs ne sont pas tous valides. */
  private buildPayload(): CompetenceSchedulePayload | null {
    const raw = this.form.getRawValue();
    const dailyHours = parseHoursLabel(raw.dailyHours);
    const hasPeriod = raw.periodStart && raw.periodEnd && new Date(raw.periodEnd) >= new Date(raw.periodStart);
    if (!hasPeriod || !raw.workedDays?.length || dailyHours === null || this.form.get('dailyHours')?.invalid) {
      return null;
    }
    return { periodStart: raw.periodStart, periodEnd: raw.periodEnd, workedDays: raw.workedDays, dailyHours };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
